import {
  defectRulePath,
  defectRulesetPath,
  defectRulesetsPath,
  defectRulesPath,
} from '../contracts/httpPaths';
import {
  DefectRuleResponseBody,
  DefectRulesetResponseBody,
  DefectRulesetsResponseBody,
  DefectRulesResponseBody,
  toDefectRulesetSlug,
} from '../contracts/defectRulesets';
import { getRefreshDataHeader } from 'utils/getRefreshDataHeader';

import { surveyDataManagerClient, toSurveyDataManagerApiError } from './httpClient';
import {
  buildMockScannerRules,
  buildMockScannerRulesets,
  isMockScannerDefectSlug,
  isScannerMockCatalogueEnabled,
} from './scannerMockResponses';

function shouldServeScannerMocks(slug: string): boolean {
  return isScannerMockCatalogueEnabled() && isMockScannerDefectSlug(slug);
}

export async function getDefectRulesets(
  slug: string,
  refreshData: boolean = false,
): Promise<DefectRulesetsResponseBody> {
  if (shouldServeScannerMocks(slug)) {
    return buildMockScannerRulesets();
  }

  try {
    const response = await surveyDataManagerClient.get<DefectRulesetsResponseBody>(
      defectRulesetsPath(slug),
      getRefreshDataHeader(refreshData),
    );
    return response.data;
  } catch (error) {
    throw toSurveyDataManagerApiError(error);
  }
}

export async function getDefectRuleset(
  slug: string,
  rulesetSlug: string,
  refreshData: boolean = false,
): Promise<DefectRulesetResponseBody> {
  if (shouldServeScannerMocks(slug)) {
    const match = buildMockScannerRulesets().rulesets.find(
      (ruleset) => ruleset.slug === toDefectRulesetSlug(rulesetSlug),
    );
    if (match) {
      return match;
    }
  }

  try {
    const response = await surveyDataManagerClient.get<DefectRulesetResponseBody>(
      defectRulesetPath(slug, rulesetSlug),
      getRefreshDataHeader(refreshData),
    );
    return response.data;
  } catch (error) {
    throw toSurveyDataManagerApiError(error);
  }
}

export interface GetDefectRulesOptions {
  readonly measurementSlug?: string;
  readonly refreshData?: boolean;
}

export async function getDefectRules(
  slug: string,
  rulesetSlug: string,
  options: GetDefectRulesOptions | boolean = false,
): Promise<DefectRulesResponseBody> {
  const resolved =
    typeof options === 'boolean'
      ? { refreshData: options, measurementSlug: undefined }
      : options;
  const refreshData = resolved.refreshData ?? false;
  const params = new URLSearchParams();
  if (resolved.measurementSlug) {
    params.set('measurement', resolved.measurementSlug);
  }

  if (shouldServeScannerMocks(slug)) {
    const mocked = buildMockScannerRules(slug, resolved.measurementSlug, rulesetSlug);
    if (mocked) {
      return mocked;
    }
  }

  try {
    const response = await surveyDataManagerClient.get<DefectRulesResponseBody>(
      defectRulesPath(slug, rulesetSlug),
      {
        ...getRefreshDataHeader(refreshData),
        params,
      },
    );
    return response.data;
  } catch (error) {
    throw toSurveyDataManagerApiError(error);
  }
}

export async function getDefectRule(
  slug: string,
  rulesetSlug: string,
  ruleSlug: string,
  refreshData: boolean = false,
): Promise<DefectRuleResponseBody> {
  if (shouldServeScannerMocks(slug)) {
    const mocked = buildMockScannerRules(slug);
    const match = mocked?.rules.find((rule) => rule.slug === ruleSlug);
    if (match) {
      return match;
    }
  }

  try {
    const response = await surveyDataManagerClient.get<DefectRuleResponseBody>(
      defectRulePath(slug, rulesetSlug, ruleSlug),
      getRefreshDataHeader(refreshData),
    );
    return response.data;
  } catch (error) {
    throw toSurveyDataManagerApiError(error);
  }
}
